import { FC, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import {
  Divider,
  HStack,
  Link,
  Spacer,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';

import { SensitiveOperationType } from '__generated__/GQL';
import useCountriesList from 'common/countries/useCountriesList';
import errorMessages from 'common/errors/locales';
import useCompanyContext from 'common/hooks/useCompanyContext';
import { SEPA_COUNTRIES_CODES } from 'common/sepa';
import { ibanSchema } from 'common/zod/schemas';
import Button from 'components/_core/Button';
import Callout from 'components/_core/Callout';
import * as Form from 'components/_core/form';
import Heading from 'components/_core/Heading';
import Text from 'components/_core/Text';
import BackButton from 'components/BackButton';
import SelectOption from 'components/SelectOption';
import Skeleton from 'components/Skeleton';
import SMSVerificationModal from 'components/SMSVerificationModal';
import useToast from 'components/ToastProvider/useToast';
import { logEvent } from 'features/Analytics/analytics';
import * as Layout from 'layouts/StepperLayout';

import routes from '../../routes';
import {
  AddBeneficiaryFormSchema,
  addBeneficiaryFormSchema,
} from './addBeneficiaryForm.schema';
import { RecipientBankDetailsDocument } from './graphql/recipientBankDetails.gql';
import { useCreateBankTransferRecipient } from './hooks/useCreateBankTransferRecipient';
import locales from './locales';
import { bicNotValid } from './utils';

const DEFAULT_COUNTRY_CODE = 'FR';
const SEPA_FAQ_LINK = 'https://help.shine.fr/fr/articles/5161289';

type SubmissionMode = 'create' | 'createAndTransfer' | null;

type CreateNewBeneficiaryProps =
  | {
      allowTransferToCreatedBeneficiary: true;
      onBackClick?: () => void;
      onTransferToCreatedBeneficiary: (bankTransferRecipientId: string) => void;
    }
  | {
      allowTransferToCreatedBeneficiary?: false;
      onBackClick?: () => void;
    };

export const CreateNewBeneficiary: FC<CreateNewBeneficiaryProps> = ({
  onBackClick,
  ...props
}) => {
  const history = useHistory();
  const { companyProfileId, companyUserId } = useCompanyContext();
  const [formBeneficiary, setFormBeneficiary] =
    useState<AddBeneficiaryFormSchema | null>(null);
  const { showToast } = useToast();
  const {
    isOpen: isSmsVerificationModalOpen,
    onClose: onCloseSmsVerificationModal,
    onToggle: onToggleSmsVerificationModal,
  } = useDisclosure({
    defaultIsOpen: false,
  });
  const [submissionMode, setSubmissionMode] = useState<SubmissionMode>(
    props.allowTransferToCreatedBeneficiary ? null : 'create',
  );

  /**
   * Note: this form doesn't have a <form> because it may be submitted in two different ways.
   * See the `form.handleSubmit()` calls.
   */
  const form = useForm<AddBeneficiaryFormSchema>({
    defaultValues: {
      country: DEFAULT_COUNTRY_CODE,
      isUserBankAccount: false,
    },
    resolver: zodResolver(addBeneficiaryFormSchema),
  });

  const countries = useCountriesList();
  const sepaCountries = useMemo(
    () =>
      countries
        .filter((country) => SEPA_COUNTRIES_CODES.includes(country.value))
        .map(({ emoji, label, value }) => ({
          key: value,
          label: `${emoji} ${label}`,
          value,
        })),
    [countries],
  );

  const { createBankTransferRecipient, loading: creatingBeneficiaryLoading } =
    useCreateBankTransferRecipient();

  const handleSuccess = async (token: string) => {
    if (!formBeneficiary) {
      throw new Error(
        'formBeneficiary should be set before calling handleSuccess',
      );
    }

    const {
      customLabel: label,
      iban,
      isUserBankAccount,
      name,
      swiftBic,
    } = formBeneficiary;

    try {
      const { data, errors } = await createBankTransferRecipient({
        input: {
          companyProfileId,
          companyUserId,
          iban,
          isUserBankAccount,
          label,
          name,
          swiftBic,
        },
        token,
      });
      const beneficiary =
        data?.createBankTransferRecipient.bankTransferRecipient;

      if (errors || !beneficiary) {
        showToast({
          message: errorMessages.localizedUnknownError,
          type: 'error',
        });
        return;
      }

      if (
        props.allowTransferToCreatedBeneficiary &&
        submissionMode === 'createAndTransfer'
      ) {
        props.onTransferToCreatedBeneficiary(
          beneficiary.bankTransferRecipientId,
        );
      } else {
        history.replace(routes.beneficiaries);
      }

      showToast({
        message: locales.formatString(locales.successMessage, {
          beneficiaryName: beneficiary.name,
        }) as string,
        title: locales.successTitle,
        type: 'success',
      });
    } catch (err) {
      showToast({
        message: bicNotValid(err)
          ? locales.swiftBicNotValid
          : errorMessages.localizedNetworkError,
        type: 'error',
      });
    }
  };

  const [isIbanValid, setIsIbanValid] = useState(false);
  const isBicDirty = form.getFieldState('swiftBic').isDirty;
  const shouldBicBeQueriedAndFilled = !isBicDirty && isIbanValid;
  const [bicHasBeenChangedByUser, setBicHasBeenChangedByUser] = useState(false);

  const [
    queryBic,
    { data: queryBicData, error: queryBicError, loading: queryBicLoading },
  ] = useLazyQuery(RecipientBankDetailsDocument, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (shouldBicBeQueriedAndFilled) {
        form.setValue('swiftBic', data.recipientBankDetails.bic || '');
      }
    },
  });

  const handleIbanChange = (iban: string) => {
    try {
      ibanSchema.parse(iban);
      setIsIbanValid(true);

      if (!isBicDirty) {
        form.setValue('swiftBic', '');
        queryBic({ variables: { iban } });
      }
    } catch (err) {
      setIsIbanValid(false);
    }
  };

  const getBicInputHint = () => {
    if (queryBicError && !isBicDirty) {
      return locales.queryBicError;
    } else if (queryBicLoading && shouldBicBeQueriedAndFilled) {
      return locales.queryBicLoading;
    }
    return undefined;
  };

  const { onChange: swiftBicOnChange, ...swiftBicField } =
    form.register('swiftBic');

  return (
    <>
      <SMSVerificationModal
        actionData={{
          beneficiaryInput: formBeneficiary,
          companyProfileId,
          companyUserId,
        }}
        actionType={SensitiveOperationType.CreateBeneficiary}
        isOpen={isSmsVerificationModalOpen}
        onClose={onCloseSmsVerificationModal}
        onSuccess={handleSuccess}
      />
      <Layout.Root>
        <Layout.Body>
          <Layout.HeaderButtonsContainer>
            <BackButton onClick={onBackClick} />
          </Layout.HeaderButtonsContainer>

          <Layout.BodyScrollableSection>
            <VStack alignItems="stretch" spacing="space-32">
              <Heading alignSelf="flex-start" size="lg" textAlign="left">
                {locales.title}
              </Heading>

              <VStack alignItems="stretch" spacing="space-20">
                <VStack align="stretch" marginBottom="space-4">
                  <Form.Field
                    error={form.formState.errors.country?.message}
                    label={locales.country}
                  >
                    <Controller
                      control={form.control}
                      name="country"
                      render={({ field }) => (
                        <Form.SunshineSelect
                          components={{ Option: SelectOption }}
                          isSearchable
                          onChange={(option) => {
                            if (option) {
                              field.onChange(option.value);
                            }
                          }}
                          options={sepaCountries}
                          value={sepaCountries.find(
                            (country) => country.value === field.value,
                          )}
                        />
                      )}
                    />
                  </Form.Field>

                  <Link
                    alignSelf="flex-start"
                    color="grey.800"
                    display="block"
                    href={SEPA_FAQ_LINK}
                    size="sm"
                    target="_blank"
                  >
                    {locales.sepaFaqButton}
                  </Link>
                </VStack>

                <Form.Field
                  error={form.formState.errors.name?.message}
                  label={locales.name}
                >
                  <Form.Input {...form.register('name')} />
                </Form.Field>

                <Form.IbanInputField
                  control={form.control}
                  name="iban"
                  onChange={handleIbanChange}
                />

                <Form.Field
                  error={form.formState.errors.swiftBic?.message}
                  hint={getBicInputHint()}
                  label={locales.swiftBic}
                >
                  {queryBicLoading && shouldBicBeQueriedAndFilled ? (
                    <HStack
                      borderColor="grey.300"
                      borderRadius="radius-6"
                      borderWidth={1}
                      height="40px"
                      padding="space-16"
                      width="100%"
                    >
                      <Skeleton height="8px" width="160px" />
                    </HStack>
                  ) : (
                    <Form.Input
                      {...swiftBicField}
                      isDisabled={
                        queryBicLoading && shouldBicBeQueriedAndFilled
                      }
                      onChange={(e) => {
                        swiftBicOnChange(e);
                        if (
                          queryBicData &&
                          !queryBicError &&
                          !bicHasBeenChangedByUser
                        ) {
                          logEvent({
                            name: 'User has changed BIC manually',
                          });
                          setBicHasBeenChangedByUser(true);
                        }
                      }}
                    />
                  )}
                </Form.Field>

                <Form.Field
                  error={form.formState.errors.customLabel?.message}
                  label={locales.label}
                >
                  <Form.Input {...form.register('customLabel')} />
                </Form.Field>

                <Controller
                  control={form.control}
                  name="isUserBankAccount"
                  render={({ field }) => (
                    <HStack align="start">
                      <Form.Checkbox
                        isChecked={field.value}
                        onChange={(event) => {
                          form.setValue(
                            'isUserBankAccount',
                            event.target.checked,
                          );
                        }}
                      />
                      <VStack align="left" gap={0}>
                        <Text color="grey.800" fontWeight="weight-500">
                          {locales.myAccount}
                        </Text>
                        <Text size="sm">{locales.myAccountDescription}</Text>
                      </VStack>
                    </HStack>
                  )}
                />
              </VStack>
            </VStack>
          </Layout.BodyScrollableSection>
          <Layout.FooterContainer>
            <Divider />
            <Layout.FooterButtonsContainer>
              {props.allowTransferToCreatedBeneficiary ? (
                <>
                  <Button
                    isLoading={
                      submissionMode === 'create' && creatingBeneficiaryLoading
                    }
                    onClick={form.handleSubmit((values) => {
                      setFormBeneficiary(values);
                      setSubmissionMode('create');
                      onToggleSmsVerificationModal();
                    })}
                    variant="inline-primary"
                  >
                    {locales.createOnly}
                  </Button>
                  <Spacer />
                  <Button
                    icon="arrow-right"
                    iconPosition="right"
                    isLoading={
                      submissionMode === 'createAndTransfer' &&
                      creatingBeneficiaryLoading
                    }
                    onClick={form.handleSubmit((values) => {
                      setFormBeneficiary(values);
                      setSubmissionMode('createAndTransfer');
                      onToggleSmsVerificationModal();
                    })}
                  >
                    {locales.createAndSend}
                  </Button>
                </>
              ) : (
                <Button
                  icon="arrow-right"
                  iconPosition="right"
                  isLoading={creatingBeneficiaryLoading}
                  onClick={form.handleSubmit((values) => {
                    setFormBeneficiary(values);
                    onToggleSmsVerificationModal();
                  })}
                >
                  {locales.create}
                </Button>
              )}
            </Layout.FooterButtonsContainer>
          </Layout.FooterContainer>
        </Layout.Body>
        <Layout.SidebarWithContent>
          <Callout>
            <Callout.Content>{locales.sepaInfoBox}</Callout.Content>
          </Callout>
        </Layout.SidebarWithContent>
      </Layout.Root>
    </>
  );
};

const CreateNewBeneficiaryPage: FC = () => {
  return <CreateNewBeneficiary allowTransferToCreatedBeneficiary={false} />;
};

export default CreateNewBeneficiaryPage;
